import { getContext } from '@/context';
import {
  TOUCHPOINT_POLL_INTERVAL_MS,
  TOUCHPOINT_MAX_ATTEMPTS,
} from '@/consts/touchpoint';

type Subscriber = (value: string | null) => void;

const readHeader = (): string | null => {
  try {
    const headers = getContext().utils.api.getHeaders?.();
    return headers?.['x-touchpoint-referrer'] ?? null;
  } catch {
    return null;
  }
};

let cached: string | null = readHeader();
let resolved = cached !== null;
let polling = false;
const subscribers = new Set<Subscriber>();

const notify = (value: string | null) => {
  subscribers.forEach((cb) => cb(value));
};

/**
 * Start a single shared polling loop. Called lazily on first consumer.
 * Subsequent calls are no-ops while polling is in flight or after resolution.
 */
const startPolling = () => {
  if (resolved || polling) return;
  polling = true;
  let attempt = 0;
  const id = setInterval(() => {
    const value = readHeader();
    attempt += 1;
    if (value !== null || attempt >= TOUCHPOINT_MAX_ATTEMPTS) {
      clearInterval(id);
      cached = value;
      resolved = true;
      polling = false;
      notify(value);
    }
  }, TOUCHPOINT_POLL_INTERVAL_MS);
};

/** Current cached touchpoint header, or null if not yet resolved. */
export const getTouchpoint = (): string | null => cached;

/** Subscribe to touchpoint resolution. Fires immediately if already resolved. */
export const subscribeTouchpoint = (cb: Subscriber): (() => void) => {
  subscribers.add(cb);
  if (resolved) {
    cb(cached);
  } else {
    startPolling();
  }
  return () => {
    subscribers.delete(cb);
  };
};
